import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable } from "rxjs";
import { map, mergeMap, catchError } from "rxjs/operators";
import * as JsEncryptModule from "jsencrypt";

// Env
import { environment } from "../../environments/environment";

// Utils
import { api } from "../utils/api";
import { constants } from "../utils/constants";
import helpers from "../utils/helpers";
import { LoginType } from "../utils/types";

// Model
import { UserLogged } from "../models/user-logged.model";
import { ICredentials } from "../interfaces/credentials.interface";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private KEY_URL = environment.API_URL + api.auth.publicKey;
  private AUTH_URL = environment.API_URL + api.auth.token;
  private PROFILING_URL = environment.API_URL + api.auth.status;
  private LOGOUT_URL = environment.API_URL + api.auth.logout;
  private encrypt = new JsEncryptModule.JSEncrypt();

  user: UserLogged = null;

  constructor(private _httpClient: HttpClient, private _router: Router) {
    this.loadStorage();
  }

  // =================================================================
  //                          Query
  // =================================================================
  public isLogged() {
    return this.user !== null;
  }
   

  // =================================================================
  //                          Auth
  // =================================================================
  /**
   * Invoca los servicios necesarios para autenticación con OAuth (publicKey-token-userLoginStatus) y setea usuario loggeado
   */
  public login(credentials: ICredentials, type: LoginType): Observable<any> {
    localStorage.setItem('timeLeft', '300');
    return this._httpClient.get(this.KEY_URL, { responseType: "text" }).pipe(
      mergeMap((key: string) =>
        this.handlePublicKeyResponse(key, credentials, type)
      ),
      mergeMap((userIdToken: string) => {
      
          return this._httpClient
          .post(this.PROFILING_URL, { userIdToken })
          .pipe(map((resp: any) => this.setUserData(resp)));
          
      }),
      catchError((err) => {
                 
          throw this.getError(this.getCodeError(err?.error?.error_description));
      })
    );
  }


  getCodeError(error:string){
    
    let codeError:any;

    var e = new String(error);
     
    try {
      if(e.trim() === 'undefined'){

        codeError = error;

      }else{

        let e = error.split("&quot;").join("").split(',');
       
        if(e[5] === 'cmmCode:SESSION_LIMIT'){
            
          codeError = 'session_limit';
          
        }else if(e[6] === 'detail:Fall&oacute; la validaci&oacute;n de contrase&ntilde;a'){
    
          codeError = 'invalid_grant';

        }else if(e[6] === 'detail:Contrase&ntilde;a bloqueada'){

          codeError = 'locked';

        }else if(e[6] === 'source:Profiling'){

          codeError = 'net';

        }else{

          codeError = 'unexpected';

        }

      }
        
        
     }
     catch (error) {
    
      codeError = 'fail';
      
     }
    

    return codeError;
  }

  /**
   * Manejador de respuesta con publicKey, retorna llamado a oauth/token con credenciales procesadas y encriptadas
   */
  private handlePublicKeyResponse(
    key: string,
    credentials: ICredentials,
    type: LoginType
  ): Observable<any> {
    //Set headers
    const headers = new HttpHeaders({
      Authorization: `Basic d2ViLWNsaWVudDp3ZWItc2VjcmV0`,
      loginType: type,
      "Content-Type": "application/x-www-form-urlencoded",
    });

    //Set username
    let username =
      type === "CLIENTE"
        ? `${credentials.ruc}#${credentials.docType}#${credentials.docNumber}`
        : `${credentials.ruc}#${credentials.companyUser}@bancofalabella.com.pe`;

    //Encrypt credentials
    this.encrypt.setPublicKey(key);
    const body = new URLSearchParams();
    body.set("grant_type", "password");
    body.set("username", this.encrypt.encrypt(username));
    body.set("password", this.encrypt.encrypt(credentials.password));


    return this._httpClient
      .post(this.AUTH_URL, body.toString(), { headers })
      .pipe(
        map((resp: any) => {
          this.user = new UserLogged();
          this.setSessionData(resp);
          return this.user.getSessionData().userIdToken;
        })
      );
  }

  /**
   * Setea datos de la session en variable user
   */
  private setSessionData(data: any) {

    
    this.user.setSessionData({
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      ttl: data.expires_in,
      userIdToken: data.user_id_token,
    });

   
  }

  /**
   * Setea datos del usuario en variable user
   */
  private setUserData(data: any) {

    this.user.setUserData({
      lastAccess: new Date(data.generic.lastAccessDateUserInChannel),
      name: data.systemUser["@name"],
      roles: helpers.processRoles(data.systemUser.roleTypes),
      permissions: helpers.processPermissions(data.systemUser.securityItems),
      administrator: data.systemUser["administrator"],
      idNumber: data.systemUser.institution.customer.identificationNumber,
      idType: data.systemUser.institution.customer.identificationType.mnemonic,
      companyName: data.systemUser.institution.customer.firstName1,
      companyId: data.systemUser.institution.sIntitutionId,
      email: data.systemUser?.electronicsContact?.emailAddressComplete,
      userId: data.systemUser?.userId,
      personId: data.systemUser?.customer?.customerId,
      firstName: data.systemUser?.customer?.firstName1,
      lastName: data.systemUser?.customer?.lastName1,
      firstLogin: data.generic?.firstLogin,
      
    });
    // Se agrega el profilingToken, disponible recién en userLoginStatus
    this.user.setSessionData({
      ...this.user.getSessionData(),
      profilingToken: data.systemUser?.token?.value,
      customToken: data.systemUser?.customer.token?.value,
      
    });
    this.setStorage();
  }

  // =================================================================
  //                          Errors
  // =================================================================
  /**
   * Obtiene el error correspondiente desde response
   */
  private getError(error: string = "unexpected") {

    switch (error) {
   
      case "session_limit":
        return constants.alerts.sessionlimit;

      case "unauthorized":
        return constants.alerts.credentials;

      case "invalid_grant":
        return constants.alerts.credentials;

      case "locked":
        return constants.alerts.bloqueo;  

      case "unexpected":
        return constants.alerts.credentials;
     
      case "undefined":  
        return constants.alerts.credentials;
     
      case "net":
          return constants.alerts.default;  

      default:
        return constants.alerts.default;
    }
  }

  public refreshToken(): Observable<string> {
    
    //Set headers
    const headers = new HttpHeaders({
      Authorization: `Basic d2ViLWNsaWVudDp3ZWItc2VjcmV0`,
      "Content-Type": "application/x-www-form-urlencoded",
    });
    const body = new URLSearchParams();
    body.set("grant_type", "refresh_token");
    body.set("refresh_token", this.user.getSessionData().refreshToken);

    return this._httpClient
      .post(this.AUTH_URL, body.toString(), { headers })
      .pipe(
        map((resp: any): string => {
          this.setSessionData(resp);
          this.setStorage();
          return this.user.getSessionData().refreshToken;
        })
      );
  }

  // =================================================================
  //                          Logout
  // =================================================================
  logout() {
    localStorage.setItem('timeLeft', '300');
    const body = {
      customerId: this.user.getUserData().personId, // Id de la persona
      customerToken: this.user.getSessionData().customToken, // Token de profiling
      userToken: this.user.getSessionData().profilingToken, // Token de profiling
      userIdToken: this.user.getSessionData().userIdToken, // userIdToken para Redis
      userId: this.user.getUserData().userId, // Id de operador
      
    }
   
        // Invocación al servicio de logout, descomentar cuando se configure el body correctamente
    this._httpClient.post(this.LOGOUT_URL, body).subscribe();

    localStorage.removeItem("user");
    this.user = null;
    this._router.navigate(["/login"]);
  }

  // =================================================================
  //                          Storage
  // =================================================================
  private loadStorage() {
   
    if (!localStorage.getItem("user")) {
      return;
    }
    const userStorage = JSON.parse(localStorage.getItem("user"));
    this.user = new UserLogged(userStorage.sessionData, userStorage.userData);

    
  }

  public setStorage() {
    localStorage.setItem("user", JSON.stringify(this.user));
  }
}
